<script setup lang="ts">
    import { ref, computed } from 'vue'
    import { useRoute } from 'vue-router'

    import { strCompare, isNullOrWhitespace } from '@common/Common'
    import { dynamicUiRoutes } from '@common/dynamicUiRoutes'

    import type { PanelistReadModel } from '@Evaluation/Estimations/Domain/readmodels'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        Visible: boolean
        WorkspaceId: number
        ProjectId: number
        TargetingEnabled: boolean
        Tags: string[]
        Panelists: PanelistReadModel[]
    }>()

    const emit = defineEmits<{
        (e: 'excludePanelist', projectUserId: number): void
        (e: 'includePanelist', projectUserId: number): void
        (e: 'showPanelistAlternativesSelector', projectUserId: number): void
        (e: 'close'): void
    }>()

    // ---------------- routing ----------------

    const route = useRoute()

    // ---------------- local UI state ----------------

    const tag = ref<string | null>(null)
    const searchPhrase = ref<string | null>(null)

    // ---------------- derived ----------------

    const workspaceId = computed(() => props.WorkspaceId)
    const tchProjectId = computed(() => props.ProjectId)

    const panelistsView = computed<PanelistReadModel[]>(() =>
    {
        let view = props.Panelists.filter(p => p.IsArchived === false)

        if (!isNullOrWhitespace(searchPhrase.value))
        {
            const phrase = searchPhrase.value!.toLowerCase()
            view = view.filter(p =>
                (`${p.Name} ${p.Email}`).toLowerCase().includes(phrase)
            )
        }

        if (tag.value)
        {
            view = view.filter(p => p.Tags.includes(tag.value!))
        }

        return view
            .slice()
            .sort((a, b) =>
                strCompare(a.Name.toLowerCase(), b.Name.toLowerCase())
            )
    })

    const tagLabel = computed(() =>
    {
        if (!isNullOrWhitespace(tag.value))
            return tag.value!

        return 'Show all'
    })

    const url_PeoplePage = computed(() =>
        dynamicUiRoutes.ProjectUsers_PeoplePage(
            route,
            workspaceId.value,
            tchProjectId.value
        )
    )

    // ---------------- actions ----------------

    function selectTag(t: string | null)
    {
        tag.value = t
    }

    function excludePanelist(projectUserId: number)
    {
        emit('excludePanelist', projectUserId)
    }

    function includePanelist(projectUserId: number)
    {
        emit('includePanelist', projectUserId)
    }

    function showPanelistAlternativesSelector(projectUserId: number)
    {
        emit('showPanelistAlternativesSelector', projectUserId)
    }

    function close()
    {
        emit('close')
    }
</script>


<template>

    <div class="modal-mask" v-show="Visible">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Add panelists</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body" style="min-height: 300px;">
                        <div v-if="Panelists.length === 0">
                            <p class="lead">
                                You currently have no Respondents set-up in the software.
                                Please go to the <a class="text-primary" v-sanitizedHref="url_PeoplePage">People section</a> to upload names & emails.
                            </p>
                        </div>

                        <div v-if="Panelists.length > 0">
                            <nav class="navbar navbar-light navbar-expand-md bg-light" role="navigation">
                                <form class="form-inline my-2 my-lg-0 ml-auto" role="search">
                                    <input class="form-control form-control-sm mr-sm-2" type="search" placeholder="Search" v-model="searchPhrase">
                                </form>
                                <form class="form-inline my-2 my-lg-0 ">
                                    <div class="dropdown">
                                        <button class="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown">
                                            <span>Filter by: {{tagLabel}}</span>
                                        </button>
                                        <div class="dropdown-menu dropdown-menu-right">
                                            <a class="dropdown-item" @click="selectTag(null)">Show all</a>
                                            <h6 class="dropdown-header">By tag:</h6>
                                            <a class="dropdown-item" v-for="t in Tags" @click="selectTag(t)">{{t}}</a>
                                        </div>
                                    </div>
                                </form>
                            </nav>

                            <table class="table">
                                <tbody>
                                    <tr v-for="p in panelistsView" :key="p.ProjectUserId">
                                        <td>
                                            <p class="fg-color-gray mb-0 mt-1">
                                                <span>{{p.Name}}</span>
                                            </p>
                                            <small class="text-muted" v-if="p.HasEmail">{{p.Email}}</small>
                                            <small class="text-danger" v-if="!p.HasEmail">Email not provided</small>
                                        </td>
                                        <td>
                                            <span class="badge badge-secondary mr-1" v-for="t in p.Tags" :key="t">{{t}}</span>
                                        </td>
                                        <td v-if="TargetingEnabled" style="width: 80px;">
                                            <button type="button" class="btn btn-outline-secondary" @click="showPanelistAlternativesSelector(p.ProjectUserId)" :disabled="!p.HasEmail">
                                                {{p.IsIncluded ? "Added" : "Add"}}
                                            </button>
                                        </td>
                                        <td v-if="!TargetingEnabled" style="width: 80px;">
                                            <button v-if="p.IsIncluded" type="button" class="btn btn-outline-danger" @click="excludePanelist(p.ProjectUserId)">Exclude</button>
                                            <button v-if="!p.IsIncluded" type="button" class="btn btn-outline-success" @click="includePanelist(p.ProjectUserId)">Include</button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <a class="btn btn-outline-secondary" v-sanitizedHref="url_PeoplePage" target="_blank">Add people</a>
                        <button @click="close" type="button" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
